import { useEffect, useRef } from 'react';

import { useDefaultContractTypeExperiment } from './useDefaultContractTypeExperiment';
import { getProductFilterForOffer } from '../../../../shared/helpers';
import { type Offer, type ProductFilter } from '../../../../shared/types';

interface UseDefaultContractTypePreSelectionParams {
  /** Whether the offers have loaded successfully. */
  isSuccessful: boolean;
  /** Offers list used by the offer step (may be filtered/sorted). */
  offers: Offer[];
  /** The currently selected discount code, if any. */
  selectedDiscountCode?: string;
  /** Sets the contract-type product filter (same handler the RadioGroup uses). */
  handleValueChange: (value: ProductFilter) => void;
}

/**
 * exp-n-611 — default contract-type experiment.
 *
 * Drives the offer step from the outside so the shared offer hooks stay experiment-agnostic:
 * removing this experiment is deleting the `exp-n-611` folder and its single call site in OfferStep.
 *
 * Variant `ON`: once offers have loaded and nothing is selected yet (e.g. a fresh visit), pre-selects
 * the *contract type* (product filter) of the first offer the backend returns, so that type's offers
 * become visible. It deliberately does NOT select a specific offer — the user still picks the offer.
 * Control (`OFF`) is a no-op, leaving the step awaiting a contract-type choice.
 *
 * The pre-selection runs at most once per mount and only when no selection exists, so it never
 * overrides a returning user's context pre-selection or a choice the user has already made.
 */
export const useDefaultContractTypePreSelection = ({
  isSuccessful,
  offers,
  selectedDiscountCode,
  handleValueChange,
}: UseDefaultContractTypePreSelectionParams) => {
  const isExperimentOn = useDefaultContractTypeExperiment();
  const hasPreSelected = useRef(false);

  useEffect(() => {
    if (!isExperimentOn || hasPreSelected.current) return;
    if (!isSuccessful || selectedDiscountCode) return;

    const firstOffer = offers[0];
    if (!firstOffer) return;

    hasPreSelected.current = true;

    // Pre-select only the contract type of the first offer — this reveals that type's offers while
    // leaving the specific offer unselected.
    handleValueChange(getProductFilterForOffer(firstOffer));
  }, [isExperimentOn, isSuccessful, offers, selectedDiscountCode, handleValueChange]);
};
